import type { i18n } from 'i18next'

export interface MissingTranslationRender {
  language: string
  namespace: string
  key: string
  pagePath?: string
  availableAt?: TranslationLocation[]
}

export interface TranslationLocation {
  language: string
  namespace: string
}

export interface MissingTranslationEvent {
  type: 'missing' | 'resolved'
  translation: MissingTranslationRender
}

export interface PlaywrightI18nGuardState {
  missing: MissingTranslationRender[]
}

interface PlaywrightI18nGuardOptions {
  sourceLocale?: string
  sourceOnlyNamespaces?: string[]
}

declare global {
  interface Window {
    __PCL_PLAYWRIGHT_I18N_GUARD__?: PlaywrightI18nGuardState
    __PCL_PLAYWRIGHT_I18N_REPORT__?: (
      event: MissingTranslationEvent,
    ) => Promise<void>
  }
}

const installedInstances = new WeakSet<i18n>()

function hasTranslationValue(instance: i18n, item: MissingTranslationRender) {
  const value = instance.getResource(item.language, item.namespace, item.key)
  if (value === undefined) return false
  if (value === null && !instance.options.returnNull) return false
  if (value === '' && !instance.options.returnEmptyString) return false
  return true
}

function locationsContainingKey(
  instance: i18n,
  missing: MissingTranslationRender,
): TranslationLocation[] {
  const locations: TranslationLocation[] = []
  for (const [language, resources] of Object.entries(instance.store.data)) {
    for (const namespace of Object.keys(resources ?? {})) {
      if (
        (language !== missing.language || namespace !== missing.namespace) &&
        hasTranslationValue(instance, { ...missing, language, namespace })
      ) {
        locations.push({ language, namespace })
      }
    }
  }
  return locations.sort((left, right) =>
    `${left.language}\0${left.namespace}`.localeCompare(
      `${right.language}\0${right.namespace}`,
    ),
  )
}

function reconcileLoadedTranslations(
  instance: i18n,
  state: PlaywrightI18nGuardState,
): void {
  const unresolved: MissingTranslationRender[] = []
  for (const item of state.missing) {
    if (hasTranslationValue(instance, item)) {
      void window.__PCL_PLAYWRIGHT_I18N_REPORT__?.({
        type: 'resolved',
        translation: item,
      })
    } else {
      unresolved.push(item)
    }
  }
  state.missing = unresolved
}

function recordMissingTranslation(
  state: PlaywrightI18nGuardState,
  item: MissingTranslationRender,
): void {
  const exists = state.missing.some(
    (candidate) =>
      candidate.language === item.language &&
      candidate.namespace === item.namespace &&
      candidate.key === item.key,
  )
  if (!exists) {
    state.missing.push(item)
    void window.__PCL_PLAYWRIGHT_I18N_REPORT__?.({
      type: 'missing',
      translation: item,
    })
  }
}

export function installPlaywrightI18nGuard(
  instance: i18n,
  options: PlaywrightI18nGuardOptions = {},
): void {
  if (typeof window === 'undefined') return
  const state = window.__PCL_PLAYWRIGHT_I18N_GUARD__
  if (!state || installedInstances.has(instance)) return
  installedInstances.add(instance)

  let syncingSourceOnly = false
  const syncedSourceOnly = new Set<string>()
  const syncSourceOnlyResources = () => {
    const activeLocale = instance.language
    if (
      syncingSourceOnly ||
      !activeLocale ||
      !options.sourceLocale ||
      activeLocale === options.sourceLocale
    ) {
      return
    }

    syncingSourceOnly = true
    try {
      for (const namespace of options.sourceOnlyNamespaces ?? []) {
        const id = `${activeLocale}\0${namespace}`
        if (syncedSourceOnly.has(id)) continue
        const source = instance.getResourceBundle(
          options.sourceLocale,
          namespace,
        )
        if (source) {
          syncedSourceOnly.add(id)
          instance.addResourceBundle(
            activeLocale,
            namespace,
            source,
            true,
            false,
          )
        }
      }
    } finally {
      syncingSourceOnly = false
    }
  }

  syncSourceOnlyResources()

  // Tests must validate the active locale rather than silently rendering the
  // source-language fallback. This instance only exists inside the browser
  // page whose fixture installed the guard marker.
  instance.options.fallbackLng = false
  instance.options.saveMissing = true

  instance.on('missingKey', (languages, namespace, key) => {
    for (const language of languages.length ? languages : [instance.language]) {
      const missing: MissingTranslationRender = {
        language,
        namespace,
        key,
        pagePath: `${window.location.pathname}${window.location.search}`,
      }
      missing.availableAt = locationsContainingKey(instance, missing)
      recordMissingTranslation(state, missing)
    }
  })

  const reconcile = () => {
    syncSourceOnlyResources()
    reconcileLoadedTranslations(instance, state)
  }
  instance.on('loaded', reconcile)
  instance.store.on('added', reconcile)
}

export function formatMissingTranslationError(
  missing: MissingTranslationRender[],
): string {
  const entries = [...missing]
    .sort((left, right) =>
      `${left.language}\0${left.namespace}\0${left.key}`.localeCompare(
        `${right.language}\0${right.namespace}\0${right.key}`,
      ),
    )
    .flatMap((item) => {
      const catalog = `public/locales/${item.language}/${item.namespace}.json`
      const sameLanguageNamespaces = (item.availableAt ?? [])
        .filter(({ language }) => language === item.language)
        .map(({ namespace }) => namespace)
      const lines = [
        `- ${item.language}/${item.namespace}: ${item.key}`,
        `  Page: ${item.pagePath ?? '(unknown)'}`,
        `  Expected: ${catalog} -> ${item.key}`,
      ]

      if (item.availableAt?.length) {
        lines.push(
          `  Same key found in: ${item.availableAt
            .map(({ language, namespace }) => `${language}/${namespace}`)
            .join(', ')}`,
        )
      }

      if (sameLanguageNamespaces.length > 0) {
        lines.push(
          `  Likely fix: request "${sameLanguageNamespaces[0]}:${item.key}", or add the key to ${catalog}.`,
        )
      } else {
        lines.push(
          `  Likely fix: add the key to ${catalog}, or correct the key/namespace passed to t().`,
        )
      }
      return lines
    })
  return [
    `Missing translations rendered (${missing.length}):`,
    ...entries,
  ].join('\n')
}

export function assertNoMissingTranslations(
  missing: MissingTranslationRender[],
): void {
  if (missing.length > 0) {
    throw new Error(formatMissingTranslationError(missing))
  }
}
